import { ChangeEvent, MouseEvent } from 'react';
import { DropdownButton, Dropdown, Form } from 'react-bootstrap';
import { HiOutlinePlus } from 'react-icons/hi';
import { MdDelete } from 'react-icons/md';
import { Plover } from '@blisspointmedia/bpm-types/dist/webTrafficOptimization';
import { Button } from '@Components/Button/Button';
import { IconButton } from '@Components/IconButton';
import { Size } from '@Components/designConstants';
import { WtoDowVariant, WtoRule, WtoVariant, WtoVariantSplit } from '../../wto-form-context';

interface VariantSplitsProps {
  variants: (WtoVariantSplit | WtoDowVariant)[];
  dowIndex?: number;
  readOnly: boolean;
  newRule: WtoRule;
  wtoFormObj: Plover;
  onSelectChange: (
    event: unknown,
    keyName?: 'additional_utm' | 'additional_utm_operation' | 'variant_id' | 'cookie_operation',
    index?: number,
    index2?: number
  ) => void;
  onInputChange: (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    valueType?: string,
    idx?: number
  ) => void;
  onDeleteOperationClick: (
    e: MouseEvent<HTMLElement>,
    ruleType:
      | 'conditions'
      | 'cookie_operation'
      | 'additional_utm'
      | 'additional_utm_operation'
      | 'variant_splits',
    optrnIdx?: number
  ) => void;
  handleDowVariant: (
    operation: 'select' | 'allocate' | 'delete',
    index: number,
    rowIndex?: number,
    data?: unknown
  ) => void;
  addTrafficSplit: () => void;
  addDowTrafficSplit: (index: number) => void;
}

export const VariantSplits = ({
  variants,
  dowIndex,
  readOnly,
  newRule,
  wtoFormObj,
  onSelectChange,
  onInputChange,
  onDeleteOperationClick,
  handleDowVariant,
  addTrafficSplit,
  addDowTrafficSplit
}: VariantSplitsProps) => {
  const variantIdProperty = dowIndex === undefined ? 'variant_id' : 'name';

  const isVariantSelected = (variantId: string) =>
    dowIndex === undefined
      ? newRule.variant_splits.some(split => split.variant_id === variantId)
      : newRule.dow_operation[dowIndex].variants.some(variant => variant.name === variantId);

  // disable variant selection if no more variants remain unselected
  // Plover.variants is only weakly typed (Record<string, any>[]) in bpm-types; every
  // variant read off wtoFormObj is actually WtoVariant-shaped at runtime.
  let selectedVariantCount = 0;
  (wtoFormObj.variants as WtoVariant[]).forEach(variant => {
    if (isVariantSelected(variant.variant_id)) {
      selectedVariantCount++;
    }
  });

  return (
    <>
      <div className='variants-input-wrapper'>
        {variants.map((split, splitIndex: number) => (
          <div className='variant-split-wrapper' key={splitIndex}>
            <DropdownButton
              className='variant-dropdown'
              data-testid={`variant-dropdown-${splitIndex}`}
              disabled={readOnly || selectedVariantCount === wtoFormObj.variants.length}
              title={split[variantIdProperty] || 'Variant ID'}
            >
              {(wtoFormObj.variants as WtoVariant[]).map(variant =>
                // if there is a DOW index, check if variant has been selected in DOW operations
                // if there is not, check if variant  has been selected in variant splits
                // only allow variants that have not been selected as options
                isVariantSelected(variant.variant_id) ? null : (
                  <Dropdown.Item
                    key={variant.variant_id}
                    eventKey={variant.variant_id}
                    data-testid={`variant-option-${variant.variant_id}`}
                    onClick={() =>
                      dowIndex === undefined
                        ? onSelectChange(variant, 'variant_id', splitIndex)
                        : handleDowVariant('select', dowIndex, splitIndex, variant)
                    }
                  >
                    {variant.variant_id}
                  </Dropdown.Item>
                )
              )}
            </DropdownButton>
            <div className='percentage-wrapper'>
              <Form.Control
                value={split.split ?? ''}
                disabled={wtoFormObj.is_deleted}
                className='variant-percentage'
                data-testid={`variant-percentage-${splitIndex}`}
                data-type='split'
                placeholder='0'
                type='number'
                onChange={e =>
                  dowIndex === undefined
                    ? onInputChange(e, 'split', splitIndex)
                    : handleDowVariant('allocate', dowIndex, splitIndex, e.target.value)
                }
              />
              %
            </div>
            {variants.length > 1 && !readOnly && (
              <IconButton
                className='delete-button'
                data-index={splitIndex}
                onClick={e => {
                  e.preventDefault();
                  if (dowIndex === undefined) {
                    onDeleteOperationClick(e, 'variant_splits');
                  } else {
                    handleDowVariant('delete', dowIndex, splitIndex);
                  }
                }}
              >
                <MdDelete />
              </IconButton>
            )}
          </div>
        ))}
      </div>
      <div className='add-split-row'>
        {variants.length < wtoFormObj.variants.length && (
          <Button
            size={Size.LG}
            disabled={readOnly}
            leadingElement={<HiOutlinePlus />}
            onClick={e => {
              e.preventDefault();
              if (dowIndex === undefined) {
                addTrafficSplit();
              } else {
                addDowTrafficSplit(dowIndex);
              }
            }}
          >
            Add Traffic Split
          </Button>
        )}
        <div className='split-total'>
          {variants.reduce((total, variant) => total + Number(variant.split || 0), 0)}%
        </div>
      </div>
    </>
  );
};
